/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import { useCallback } from "react";

interface ExternalLinkOptions {
  target?: "_blank" | "_self" | "_parent" | "_top";
  rel?: string;
  download?: string;
}

/**
 * Hook for handling external links and downloads
 * Provides a clean interface for opening external URLs or triggering downloads
 */
export function useExternalLink() {
  const openExternalLink = useCallback(
    (url: string, options: ExternalLinkOptions = {}) => {
      const {
        target = "_blank",
        rel = "noopener noreferrer",
        download,
      } = options;

      // Create a temporary link element
      const link = document.createElement("a");
      link.href = url;
      link.target = target;
      link.rel = rel;

      if (download) {
        link.download = download;
      }

      // Append to DOM, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    [],
  );

  const downloadFile = useCallback(
    (url: string, filename?: string) => {
      openExternalLink(url, {
        target: "_blank",
        ...(filename && { download: filename }),
      });
    },
    [openExternalLink],
  );

  const openInNewTab = useCallback(
    (url: string) => {
      openExternalLink(url, {
        target: "_blank",
        rel: "noopener noreferrer",
      });
    },
    [openExternalLink],
  );

  return {
    openExternalLink,
    downloadFile,
    openInNewTab,
  };
}
